<template>
  <CContainer
    class="pass-history-view datatable-container px-0"
    @click="onContainerClick"
  >
    <Loader :is-processing="isLoading" />
    <!-- Edit pass time modal -->
    <CModal
      backdrop="static"
      :visible="modal.isVisible.passTimeChange"
      :close-on-backdrop="false"
    >
      <div class="pt-4 px-4 create-popup-header">
        <div
          class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer"
          @click="closeModal()"
        >
          <span class="close-icon" />
        </div>
      </div>
      <div v-if="modal.activePass">
        <ChangePassHistoryTimeForm
          :pass="modal.activePass"
          @pass-time-change="onPassTimeChange"
        />
      </div>
      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- Student passes history -->
    <CModal
      backdrop="static"
      v-if="modal.activePass && modal.isVisible.todayPasses"
      :visible="modal.isVisible.todayPasses"
      :close-on-backdrop="false"
      class="p-0"
      size="lg"
      add-content-classes="no-padding-modal no-border-radius-modal left-top-header"
    >
      <div class="pt-4 px-3 create-popup-header">
        <div
          class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer"
          @click="closeModal()"
        >
          <span class="close-icon" />
        </div>
      </div>
      <div>
        <StudentPassHistory :user-id="modal.activePass.user.id" />
      </div>
      <template #footer-wrapper>
        <div />
      </template>
    </CModal>
    <!-- End modals -->

    <CRow class="justify-content-center mt-0 mt-md-4">
      <CCol md="12" class="mb-4 mb-md-5">
        <h1 class="text-center">
          Pass History
          <HelpCenterButton classes="ms-2" content-key="pass_history" />
        </h1>
      </CCol>
      <CCol md="4">
        <LazyLoadSelect
          :is-multiple="true"
          classes="mb-3"
          :selected="filterParams.by_students"
          type="student"
          placeholder="Search student(s)"
          @changed="
            (value) => {
              filterParams.by_students = value
            }
          "
        />

        <VSelect
          v-if="gradeYears"
          v-model="filterParams.grade_years"
          class="cs-select mb-3 ps__child--consume"
          :options="gradeYears"
          placeholder="Select year"
          :clearable="false"
          :searchable="false"
          @update:model-value="setCalendarDates"
        />
      </CCol>
      <CCol md="4">
        <LazyLoadSelect
          :reports="true"
          :is-multiple="true"
          classes="mb-3"
          :selected="filterParams.by_teachers"
          type="adults"
          placeholder="Search teacher(s)"
          @changed="
            (value) => {
              filterParams.by_teachers = value
            }
          "
        />

        <DatePicker
          v-if="filterParams.grade_years"
          v-model="filterParams.dates"
          class="cs-date-picker"
          :masks="{ input: 'MM/DD/YYYY' }"
          placeholder="Year"
          mode="range"
          is-range
          :min-date="minDate"
          :max-date="maxDate"
        >
          <template #default="{ inputValue, togglePopover, hidePopover }">
            <div class="input-icon-field cursor-pointer form-group">
              <CFormInput
                placeholder="Date"
                type="readonly"
                :value="inputValue.start + '-' + inputValue.end"
                readonly
                class="date-calendar-option cursor-pointer"
                @click="showCalendarRanges(hidePopover, togglePopover)"
              />
              <span
                class="field-icon"
                @click="showCalendarRanges(hidePopover, togglePopover)"
              >
                <i class="fi flaticon-calendar me-1" />
              </span>
              <div
                v-show="calendar.isRangesVisible"
                class="optional-date-ranges-container"
              >
                <div
                  v-for="(range, index) in optionalDateRanges"
                  :key="index"
                  :class="{
                    active: range.label === calendar.activeRange.label
                  }"
                  class="option-range p-2"
                  @click="setCalendarRange(range, togglePopover)"
                >
                  {{ range.label }}
                </div>
              </div>
            </div>
          </template>
        </DatePicker>
      </CCol>
      <CCol md="4">
        <LazyLoadSelect
          :is-multiple="true"
          :is-processing="false"
          classes="mb-3"
          :selected="filterParams.by_rooms"
          type="locations"
          placeholder="Search room(s)"
          @changed="
            (value) => {
              filterParams.by_rooms = value
            }
          "
        />
      </CCol>
      <CCol md="12" class="mt-1 mt-md-4 filter-actions">
        <div
          class="d-sm-inline-block text-center"
          @click="advancedFilter(false)"
        >
          <CButton class="btn bg-gradient-blue reverse text-white">
            <div class="p-small">Pass view</div>
          </CButton>
        </div>
        <div class="d-sm-inline-block text-center">
          <CButton
            class="mx-2 my-2 btn bg-neutral-blue py-2 text-white"
            @click="advancedFilter(true)"
          >
            Tabular view <br />(CSV)
          </CButton>
        </div>
        <div class="d-sm-inline-block text-center">
          <CButton
            class="me-0 me-md-2 p-small bg-gradient-blue reverse"
            @click="clearFilter()"
          >
            <span class="btn bg-white px-4"
              ><b class="text-graident-blue reverse">Clear</b></span
            >
          </CButton>
        </div>
      </CCol>
    </CRow>

    <CRow class="mt-5">
      <CCol md="12">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div
            v-for="(tab, index) in tableTabs"
            :key="index"
            class="table-tab px-2 px-md-4"
            :class="{ active: tab.value === activeTableTab.value }"
          >
            {{ tab.label }}
          </div>
          <div class="actions d-flex align-items-center">
            <CLink
              class="lh-normal text-accent-blue-light ms-2 ms-md-0"
              download="pass-history.csv"
              target="_blank"
              @click="requestPassHistory(pagination.activePage, true)"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </CLink>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>

                <CFormInput
                  placeholder="Search..."
                  v-model="columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div class="status-guides-container flex-wrap flex-md-nowrap mb-2">
          <div class="d-flex align-items-center flex-wrap">
            <div class="tabs-filter-container me-4">
              <div
                class="filter-tab"
                :class="{
                  active: filterParams.tab_filter === 'only_my_passes'
                }"
                @click="setTabFilterValue('only_my_passes')"
              >
                My passes
              </div>
              <div
                v-if="isUserInTransparency"
                class="filter-tab"
                :class="{ active: filterParams.tab_filter === 'all_passes' }"
                @click="setTabFilterValue('all_passes')"
              >
                All passes
              </div>
            </div>
            <div class="status-guide d-block py-4 p-md-0">
              <div class="d-flex align-items-center">
                <div class="me-2">Filters:</div>
                <div class="status-list flex-wrap flex-md-nowrap">
                  <CCheckBox
                    v-if="passesTimeSettings && passesTimeSettings.min_time"
                    :label="
                      $helpers.formatDate(
                        passesTimeSettings.min_time,
                        'hh:mm:ss',
                        'mm'
                      ) + '+Min'
                    "
                    v-model="filterParams.checkBoxes.yellow_time"
                    name="Extended Passes"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox min-10"
                    @update:model-value="requestPassHistory()"
                  />
                  <CCheckBox
                    label="System Ended"
                    v-model="filterParams.checkBoxes.system_ended"
                    name="System Ended"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox system-ended"
                    @update:model-value="requestPassHistory()"
                  />
                  <CCheckBox
                    label="Extended Passes"
                    v-model="filterParams.checkBoxes.extended_time"
                    name="Extended Passes"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox extended-passes"
                    @update:model-value="requestPassHistory()"
                  />
                  <CCheckBox
                    label="Canceled Apt"
                    v-model="filterParams.checkBoxes.only_canceled_passes"
                    name="Canceled"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox edited"
                    @update:model-value="requestPassHistory()"
                  />
                  <CCheckBox
                    label="Missed Apt"
                    v-model="filterParams.checkBoxes.only_missed_passes"
                    name="Missed"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox edited"
                    @update:model-value="requestPassHistory()"
                  />
                  <CCheckBox
                    label="Edited"
                    v-model="filterParams.checkBoxes.edited"
                    name="Edited"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox edited"
                    @update:model-value="requestPassHistory()"
                  />
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div class="me-2">Optional:</div>
                <div class="status-list flex-wrap flex-md-nowrap">
                  <CCheckBox
                    label="Student Number"
                    v-model="showStudentNumber"
                    name="Student number"
                    :inline="true"
                    :custom="true"
                    class="custom-checkbox m-0"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="per-page-container">
            <per-page-selector :pagination="pagination" :action="setPerPage" />
          </div>
        </div>

        <DataTable
          v-if="passHistoriesCollection"
          id="pass-history-datatable"
          class="datatable bg-white"
          :items="passHistoriesCollection"
          :fields="activeDataTableFields"
          :column-filter="isTabular ? { external: true } : false"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #created_date="{ item }">
            <td>{{ item.created_date ? item.created_date : "-" }}</td>
          </template>
          <template #created_date-filter>
            <input
              v-model="columnSearchKeys.created_date.value"
              placeholder="Search by date"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #user="{ item }">
            <td class="p-0 position-relative td-box w-auto d-table-cell">
              <div
                class="d-flex align-items-center justify-content-start user-details-column"
                :class="getPassStatus(item) ? getPassStatus(item).class : ''"
              >
                <div v-if="item.user" class="d-flex align-items-center">
                  <div
                    v-if="getPassStatus(item)"
                    class="pass-status"
                    :class="getPassStatus(item).class"
                  >
                    <!-- v-show="item.completed_at && item.pass_status === 0" -->
                    {{ getPassStatus(item).label }}
                  </div>
                  <div class="edit me-2">
                    <div class="d-flex align-items-center content-center mb-1">
                      <span
                        v-if="
                          isPassEditable(item) &&
                          getPassStatus(item).label != 'Missed Apt'
                        "
                        class="edit-btn"
                        @click="setActiveModalPass(item, 'passTimeChange')"
                        ><i class="fi flaticon-edit me-1"
                      /></span>
                      <span
                        v-if="getFlagStatusColor(item)"
                        class="status-flag"
                        :style="'color:' + getFlagStatusColor(item)"
                        ><i class="flaticon-flag-2"
                      /></span>
                    </div>
                    <p v-if="item.edited_at" class="is-edited d-flex m-0 mt-2">
                      EDITED<i class="fi flaticon-edit ms-1" />
                    </p>
                  </div>
                  <div
                    v-show="showStudentPhotos"
                    class="c-avatar c-avatar-pass-history me-2"
                  >
                    <img
                      :src="
                        item.user.avatar
                          ? item.user.avatar
                          : 'img/avatars/user-avatar.jpeg'
                      "
                      class="c-avatar-pass-history-img"
                    />
                  </div>
                  <div class="user-info">
                    <b>{{ item.user.first_name }} {{ item.user.last_name }}</b>
                  </div>
                </div>
              </div>
            </td>
          </template>

          <template #user-filter>
            <input
              v-model="columnSearchKeys.user.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #first_name-filter>
            <input
              v-model="columnSearchKeys.first_name.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #last_name-filter>
            <input
              v-model="columnSearchKeys.last_name.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #email-filter>
            <input
              v-model="columnSearchKeys.email.value"
              placeholder="Search by email"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #out_location-filter>
            <input
              v-model="columnSearchKeys.out_location.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #out_teacher-filter>
            <input
              v-model="columnSearchKeys.out_teacher.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #out_time-filter>
            <input
              v-model="columnSearchKeys.out_time.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #in_location-filter>
            <input
              v-model="columnSearchKeys.in_location.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #in_teacher-filter>
            <input
              v-model="columnSearchKeys.in_teacher.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #in_time-filter>
            <input
              v-model="columnSearchKeys.in_time.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_out_location-filter>
            <input
              v-model="columnSearchKeys.child_out_location.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_out_teacher-filter>
            <input
              v-model="columnSearchKeys.child_out_teacher.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_out_time-filter>
            <input
              v-model="columnSearchKeys.child_out_time.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_in_location-filter>
            <input
              v-model="columnSearchKeys.child_in_location.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_in_teacher-filter>
            <input
              v-model="columnSearchKeys.child_in_teacher.value"
              placeholder="Search by name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_in_time-filter>
            <input
              v-model="columnSearchKeys.child_in_time.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #approved_at="{ item }">
            <td class="pass-time-info">
              <b>{{ item.from ? item.from.name : "-" }}</b>
              <p class="my-1 mx-0 p-0">
                {{
                  item.approved_at
                    ? $helpers.transformDate(item.approved_at, "h:mm A")
                    : "-"
                }}
              </p>
              <InOutColumn :pass="item" section="approved_at" />
            </td>
          </template>
          <template #approved_at-filter>
            <input
              v-model="columnSearchKeys.approved_at.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #completed_at="{ item }">
            <td class="pass-time-info in-time">
              <b>{{ item.to ? item.to.name : "-" }}</b>
              <p class="my-1 mx-0 p-0">
                {{
                  item.completed_at
                    ? $helpers.transformDate(item.completed_at, "h:mm A")
                    : "-"
                }}
              </p>
              <InOutColumn :pass="item" section="completed_at" />
            </td>
          </template>

          <template #completed_at-filter>
            <input
              v-model="columnSearchKeys.completed_at.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_approved_at="{ item }">
            <td class="pass-time-info">
              <b>{{
                item.child && item.child.from ? item.child.from.name : "-"
              }}</b>
              <p class="my-1 mx-0 p-0">
                {{
                  item.child && item.child.approved_at
                    ? $helpers.transformDate(item.child.approved_at, "h:mm A")
                    : "-"
                }}
              </p>
              <InOutColumn :pass="item" section="child_approved_at" />
            </td>
          </template>
          <template #child_approved_at-filter>
            <input
              v-model="columnSearchKeys.child_approved_at.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #child_completed_at="{ item }">
            <td class="pass-time-info in-time">
              <b>{{
                item.child && item.child.to ? item.child.to.name : "-"
              }}</b>
              <p class="my-1 mx-0 p-0">
                {{
                  item.child && item.child.completed_at
                    ? $helpers.transformDate(item.child.completed_at, "h:mm A")
                    : "-"
                }}
              </p>
              <InOutColumn :pass="item" section="child_completed_at" />
            </td>
          </template>
          <template #child_completed_at-filter>
            <input
              v-model="columnSearchKeys.child_completed_at.value"
              placeholder="Search by time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #student_sis_id="{ item }">
            <td>
              <div>
                {{
                  item.user && item.user.student_sis_id
                    ? item.user.student_sis_id
                    : ""
                }}
              </div>
            </td>
          </template>

          <template #total_time="{ item }">
            <td>
              <b>{{ item.total_time ? item.total_time : "-" }}</b>
            </td>
          </template>
          <template #total_time-filter>
            <input
              v-model="columnSearchKeys.total_time.value"
              placeholder="Search by total time"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #type="{ item }">
            <td>
              <b>{{ item.type ? item.type : "-" }}</b>
            </td>
          </template>
          <template #type-filter>
            <input
              v-model="columnSearchKeys.type.value"
              placeholder="Search by type"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #comments="{ item }">
            <td>
              <PassComments
                @comment-created="onCommentCreated"
                :pass="item"
                :is-tabular="isTabular"
                commentable-type="App\Models\Pass"
              />
            </td>
          </template>
          <template #comments-filter>
            <input
              v-model="columnSearchKeys.comments.value"
              placeholder="Search by comments"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>

          <template #expired_at="{ item }">
            <td>
              {{
                item.expired_at ? $helpers.transformDate(item.expired_at) : "-"
              }}
            </td>
          </template>
          <template #expired_at-filter>
            <input
              v-model="columnSearchKeys.expired_at.value"
              placeholder="Search by date"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>
        </DataTable>

        <Pagination
          v-if="
            pagination &&
            pagination.pages &&
            pagination.pages > 1 &&
            !isLazyLoadingMode
          "
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>
<script>
import Loader from "@/components/Loader"
import ChangePassHistoryTimeForm from "@/components/Forms/ChangePassHistoryTimeForm"

import { mapGetters } from "vuex"
import StudentPassHistory from "@/components/DataTables/StudentPassHistory"
import InOutColumn from "./ColumnComponents/InOutColumn.vue"

import moment from "moment-timezone"

import LazyLoadSelect from "@/components/LazyLoadSelect.vue"
import PerPageSelector from "../PerPageSelector.vue"
import PassComments from "./ColumnComponents/PassComments.vue"
import HelpCenterButton from "@/components/HelpCenterButton"

export default {
  name: "PassHistoryDataTable",
  components: {
    Loader,
    ChangePassHistoryTimeForm,
    StudentPassHistory,
    InOutColumn,
    LazyLoadSelect,
    PerPageSelector,
    PassComments,
    HelpCenterButton
  },
  data() {
    return {
      today: moment().format("MMM DD YYYY"),
      isLazyLoadingMode: false,
      calendar: {
        isCalendarVisible: false,
        isRangesVisible: false,
        activeRange: {
          label: "Today",
          range: {
            start: moment().format("MMM DD YYYY"),
            end: moment().format("MMM DD YYYY")
          }
        }
      },
      columnSearchKeys: {
        globalQuery: "",
        created_date: {
          value: "",
          column: ["created_date"],
          sortBy: "created_date"
        },
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        user: { value: "", column: ["user.name"], sortBy: "user.last_name" },
        first_name: {
          value: "",
          column: ["user.first_name"],
          sortBy: "user.first_name"
        },
        last_name: {
          value: "",
          column: ["user.last_name"],
          sortBy: "user.last_name"
        },
        email: { value: "", column: ["user.email"], sortBy: "user.email" },

        out_location: { value: "", column: ["from.name"], sortBy: "from.name" },
        out_teacher: {
          value: "",
          column: ["approved_by_user.first_name"],
          sortBy: "approved_by_user.first_name"
        },
        out_time: {
          value: "",
          column: ["tabular_view.approve_time"],
          sortBy: "elastic_advanced_fields.approve_time_only"
        },
        in_location: { value: "", column: ["to.name"], sortBy: "to.name" },
        in_teacher: {
          value: "",
          column: ["completed_by_user.first_name"],
          sortBy: "completed_by_user.first_name"
        },
        in_time: {
          value: "",
          column: ["tabular_view.completed_time"],
          sortBy: "elastic_advanced_fields.completed_time_only"
        },

        child_out_location: {
          value: "",
          column: ["child.from.name"],
          sortBy: "child.from.name"
        },
        child_out_teacher: {
          value: "",
          column: ["child.approved_by_user.first_name"],
          sortBy: "child.approved_by_user.first_name"
        },
        child_out_time: {
          value: "",
          column: ["tabular_view.approve_child_time"],
          sortBy: "elastic_advanced_fields.approve_child_time_only"
        },
        child_in_location: {
          value: "",
          column: ["child.to.name"],
          sortBy: "child.to.name"
        },
        child_in_teacher: {
          value: "",
          column: ["child.completed_by_user.first_name"],
          sortBy: "child.completed_by_user.first_name"
        },
        child_in_time: {
          value: "",
          column: ["tabular_view.completed_child_time"],
          sortBy: "elastic_advanced_fields.completed_child_time_only"
        },

        approved_at: {
          value: "",
          column: ["from.name"],
          sortBy: "elastic_advanced_fields.approve_time_only"
        },
        completed_at: {
          value: "",
          column: ["to.name"],
          sortBy: "elastic_advanced_fields.completed_time_only"
        },
        child_approved_at: {
          value: "",
          column: ["child.from.name"],
          sortBy: "elastic_advanced_fields.approve_child_time_only"
        },
        child_completed_at: {
          value: "",
          column: ["child.to.name"],
          sortBy: "elastic_advanced_fields.completed_child_time_only"
        },
        total_time: { value: "", column: ["total_time"], sortBy: "total_time" },
        type: { value: "", column: ["type"], sortBy: "type" },
        comments: {
          value: "",
          column: ["comments.comment"],
          sortBy: "created_date"
        },
        expired_at: { value: "", column: ["expired_at"], sortBy: "expired_at" }
      },
      filterParams: {
        search_query: "",
        sort_query: "",
        by_students: [],
        by_teachers: [],
        by_rooms: [],
        dates: {
          end: moment().format("MMM DD YYYY"),
          start: moment().format("MMM DD YYYY")
        },
        grade_years: [],
        tab_filter: "only_my_passes",
        checkBoxes: {
          edited: false,
          system_ended: false,
          extended_time: false,
          yellow_time: false,
          only_canceled_passes: false,
          missed_request: false,
          only_missed_passes: false
        }
      },
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      dataTableFields: [
        {
          key: "created_date",
          label: "Date",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "total_time",
          label: "Total",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "student_sis_id",
          filter: false,
          sorter: false,
          label: "Student number",
          _style: "min-width:150px"
        },
        {
          key: "first_name",
          label: "First name",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "last_name",
          label: "Last name",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "email",
          label: "Email",
          type: "tabular",
          _style: "min-width:150px"
        },

        {
          key: "out_location",
          label: "Out Location",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "out_teacher",
          label: "Out Teacher",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "out_time",
          label: "Out Time",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "in_location",
          label: "In Location",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "in_teacher",
          label: "In Teacher",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "in_time",
          label: "In Time",
          type: "tabular",
          _style: "min-width:150px"
        },

        {
          key: "child_out_location",
          label: "Out Location",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "child_out_teacher",
          label: "Out Teacher",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "child_out_time",
          label: "Out Time",
          type: "tabular",
          _style: "min-width:150px"
        },

        {
          key: "child_in_location",
          label: "In Location",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "child_in_teacher",
          label: "In Teacher",
          type: "tabular",
          _style: "min-width:150px"
        },
        {
          key: "child_in_time",
          label: "In Time",
          type: "tabular",
          _style: "min-width:150px"
        },

        {
          key: "user",
          label: "Student",
          _style: "min-width:280px",
          type: "pass-view"
        },
        { key: "created_date", label: "Date" },
        {
          key: "approved_at",
          _style: "min-width:130px",
          label: "Out time",
          type: "pass-view"
        },
        {
          key: "completed_at",
          _style: "min-width:130px",
          label: "In time",
          type: "pass-view"
        },
        {
          key: "child_approved_at",
          _style: "min-width:130px",
          label: "Out time",
          type: "pass-view"
        },
        {
          key: "child_completed_at",
          _style: "min-width:130px",
          label: "In time",
          type: "pass-view"
        },
        { key: "total_time", label: "Total", type: "pass-view" },
        { key: "type" },
        {
          key: "comments",
          _style: "min-width:160px",
          label: "Status/Comments"
        },
        {
          key: "expired_at",
          label: "Archived date",
          type: "tabular",
          _style: "min-width:150px"
        }
      ],
      tableTabs: [
        {
          label: "Pass History",
          value: "pass-history"
        }
      ],
      activeTableTab: "",
      isTabular: false,
      isLoading: false,
      isPassView: false,
      modal: {
        isVisible: {
          passTimeChange: false,
          comments: false,
          todayPasses: false
        },
        activePass: null
      },
      searchTimeOut: null,
      showStudentNumber: false
    }
  },
  computed: {
    ...mapGetters({
      passHistories: "passes/passHistories",
      passesTimeSettings: "passes/passesTimeSettings",
      currentUser: "authentication/user",
      showStudentPhotos: "schools/showStudentPhotos",
      isUserInTransparency: "authentication/isUserInTransparency",
      autoPassLimits: "rooms/autoPassLimits"
    }),
    passHistoriesCollection() {
      return this.passHistories
        ? this.passHistories.map((item) => {
            return {
              ...item,
              first_name: item.user ? item.user.first_name : "",
              last_name: item.user ? item.user.last_name : "",
              email: item.user ? item.user.email : "",
              out_location: item.from ? item.from.name : "-",
              out_teacher: item.approved_by_user
                ? item.approved_by_user.name
                : "-",
              out_time: item.approved_at
                ? this.$helpers.transformDate(item.approved_at, "h:mm:ss A")
                : "-",
              in_location: item.to ? item.to.name : "-",
              in_teacher: item.completed_by_user
                ? item.completed_by_user.name
                : "-",
              in_time: item.completed_at
                ? this.$helpers.transformDate(item.completed_at, "h:mm:ss A")
                : "-",
              child_out_location:
                item.child && item.child.from ? item.child.from.name : "-",
              child_out_teacher:
                item.child && item.child.approved_by_user
                  ? item.child.approved_by_user.name
                  : "-",
              child_out_time:
                item.child && item.child.approved_at
                  ? this.$helpers.transformDate(
                      item.child.approved_at,
                      "h:mm:ss A"
                    )
                  : "-",
              child_in_location:
                item.child && item.child.to ? item.child.to.name : "-",
              child_in_teacher:
                item.child && item.child.completed_by_user
                  ? item.child.completed_by_user.name
                  : "-",
              child_in_time:
                item.child && item.child.completed_at
                  ? this.$helpers.transformDate(
                      item.child.completed_at,
                      "h:mm:ss A"
                    )
                  : "-",

              child_approved_at:
                item.child && item.child.from ? item.child.from.name : "-",
              child_completed_at:
                item.child && item.child.to ? item.child.to.name : "-",
              student_sis_id: item.user ? item.user.student_sis_id : ""
            }
          })
        : this.passHistories
    },
    datesParam() {
      return this.filterParams.dates
        ? this.$helpers.currTzDate(this.filterParams.dates.start) +
            "," +
            this.$helpers.currTzDate(this.filterParams.dates.end)
        : null
    },
    activeDataTableFields() {
      const fields = this.dataTableFields.filter((field) =>
        !this.showStudentNumber ? field.key !== "student_sis_id" : field
      )
      return fields.filter((field) =>
        this.isTabular ? field.type !== "pass-view" : field.type !== "tabular"
      )
    },
    gradeYears() {
      const gradeYears = []
      const currentYear = new Date().getFullYear()
      const today = moment().format("YYYY-MM-DD HH:mm:ss")
      const newSchoolYear = moment(today).isAfter(
        moment(`${currentYear}-06-30 23:59:59`)
      )
      if (moment(today).year() == currentYear && newSchoolYear) {
        for (let i = 0; i <= currentYear - 2021; i++) {
          gradeYears.push({
            value: [2021 + i, 2022 + i],
            label: `${2021 + i}-${22 + i}`
          })
        }
      } else {
        for (let i = 0; i < currentYear - 2021; i++) {
          gradeYears.push({
            value: [2021 + i, 2022 + i],
            label: `${2021 + i}-${22 + i}`
          })
        }
      }
      return gradeYears
    },
    maxDate() {
      if (this.filterParams.grade_years.value) {
        return new Date(this.filterParams.grade_years.value[1], 5, 30)
      }
      return new Date()
      //return new Date().getFullYear() === this.filterParams.grade_years.value[1] ? this.today : new Date(this.filterParams.grade_years.value[1],5,30);
    },
    minDate() {
      if (this.filterParams.grade_years.value) {
        return new Date(this.filterParams.grade_years.value[0], 6, 1)
      }
      return new Date()
    },
    optionalDateRanges() {
      if (this.filterParams.grade_years.value && this.minDate) {
        return [
          {
            label: "Today",
            range: {
              start: moment().format("MMM DD YYYY"),
              end: moment().format("MMM DD YYYY")
            }
          },
          {
            label: "Yesterday",
            range: {
              start: moment().subtract(1, "days").format("MMM DD YYYY"),
              end: moment().subtract(1, "days").format("MMM DD YYYY")
            }
          },
          {
            label: "Last 7 days",
            range: {
              start:
                moment().subtract(7, "days") < moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment().subtract(7, "days").format("MMM DD YYYY"),
              end: moment().format("MMM DD YYYY")
            }
          },
          {
            label: "Last 14 days",
            range: {
              start:
                moment().subtract(14, "days") < moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment().subtract(14, "days").format("MMM DD YYYY"),
              //start: moment().subtract(14, 'days').format('MMM DD YYYY') >= moment(this.minDate).format('MMM DD YYYY') ? moment().subtract(14, 'days').format('MMM DD YYYY') : moment(this.minDate).format('MMM DD YYYY'),
              end: moment().format("MMM DD YYYY")
            }
          },
          {
            label: "Last 30 days",
            range: {
              start:
                moment().subtract(30, "days") < moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment().subtract(30, "days").format("MMM DD YYYY"),
              end: moment().format("MMM DD YYYY")
            }
          },
          {
            label: "This month",
            range: {
              start: moment().startOf("month").format("MMM DD YYYY"),
              end: moment().endOf("month").format("MMM DD YYYY")
            }
          },
          {
            label: "This year",
            range: {
              start:
                moment().startOf("year").add(6, "M").subtract(1, "Y") <
                moment(this.minDate)
                  ? new Date(this.minDate)
                  : moment()
                      .startOf("year")
                      .add(6, "M")
                      .subtract(1, "Y")
                      .format("MMM DD YYYY"),
              end:
                new Date().getFullYear() ==
                this.filterParams.grade_years.value[0]
                  ? new Date(this.maxDate)
                  : moment()
                      .startOf("year")
                      .add(5, "M")
                      .add(29, "days")
                      .format("MMM DD YYYY")
              //end: moment().format('MMM DD YYYY')
            }
          },
          {
            label: "Custom range",
            range: null
          }
        ]
      }
      return []
    }
  },
  created() {
    this.$store.dispatch("rooms/getAutoPassLimit")
    this.initDataSet()
    this.requestPassHistory()
  },
  mounted() {
    this.initLazyLoading()
  },
  methods: {
    requestPassHistory(page, isExport, isLazyLoadingMode, noLoader) {
      this.isLoading = !noLoader
      this.pagination.activePage = page ? page : 1
      const params = {
        only_my_passes: this.filterParams.tab_filter === "only_my_passes",
        page: this.pagination.activePage,
        per_page: this.pagination.per_page.value,
        dates: this.datesParam
      }
      if (this.filterParams.search_query) {
        params.search_query = this.filterParams.search_query
        if (this.columnSearchKeys.globalQuery) {
          params.global_search = true
        }
      }
      if (
        this.filterParams.by_students &&
        this.filterParams.by_students.length
      ) {
        params.by_students = this.filterParams.by_students
          .map((el) => el.value.id)
          .join()
      }
      if (
        this.filterParams.by_teachers &&
        this.filterParams.by_teachers.length
      ) {
        params.by_teachers = this.filterParams.by_teachers
          .map((el) => el.value.id)
          .join()
      }
      if (this.filterParams.by_rooms && this.filterParams.by_rooms.length) {
        params.by_rooms = this.filterParams.by_rooms
          .map((el) => el.value.id)
          .join()
      }
      if (this.filterParams.sort_query) {
        params.sort = this.filterParams.sort_query
      }
      const filters_list = []
      for (const key in this.filterParams.checkBoxes) {
        if (Object.hasOwnProperty.call(this.filterParams.checkBoxes, key)) {
          const item = this.filterParams.checkBoxes[key]
          if (item) {
            filters_list.push('"' + key + '"')
          }
        }
      }
      if (filters_list.length) {
        params.filters_list = "[" + filters_list + "]"
      }

      if (isExport) {
        this.getCSVPassHistoriesExport(params)
      } else {
        this.getPassHistories(params, isLazyLoadingMode)
      }
    },
    getPassHistories(params, isLazyLoadingMode) {
      this.isLoading = true
      this.$store
        .dispatch("passes/getPassHistories", params)
        .then((response) => {
          const data = response.data
          if (data.data) {
            if (isLazyLoadingMode) {
              this.$store.commit("passes/PUSH_PASS_HISTORIES", data.data)
            } else {
              this.$store.commit("passes/SET_PASS_HISTORIES", data.data)
            }
          }
          if (data.meta) {
            this.pagination.total = data.meta.total
            this.pagination.from = data.meta.from
            this.pagination.to = data.meta.to
            this.pagination.pages = Math.ceil(
              data.meta.total / data.meta.per_page
            )
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    getCSVPassHistoriesExport(params) {
      this.isLoading = true
      this.$store
        .dispatch("passes/getCSVPassHistoriesExport", {
          ...params,
          student_sis_id: this.showStudentNumber
        })
        .then((response) => {
          if (response.data) {
            this.downloadCSVExport(response.data)
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    downloadCSVExport(data) {
      const csvDataForExport = "data:text/csv;charset=utf-8," + data
      const encodedUri = encodeURI(csvDataForExport)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("target", "_blank")
      link.setAttribute("download", "pass-histories.csv")
      document.body.appendChild(link)
      link.click()
      // window.open(encodedUri);
    },
    clearFilter() {
      this.clearFilterParams()
      this.requestPassHistory()
      this.isTabular = false
    },
    clearFilterParams() {
      this.isPassView = false
      this.filterParams = {
        search_query: this.filterParams.search_query,
        sort_query: this.filterParams.sort_query,
        by_students: [],
        by_teachers: [],
        by_rooms: [],
        dates: {
          end: moment().format("MMM DD YYYY"),
          start: moment().format("MMM DD YYYY")
        },
        tab_filter: this.filterParams.tab_filter,
        grade_years: this.gradeYears
          ? this.gradeYears[this.gradeYears.length - 1]
          : [],
        checkBoxes: this.filterParams.checkBoxes
      }
      this.calendar.activeRange = this.optionalDateRanges[0]
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.requestPassHistory()
      }
    },
    getFlagStatusColor(pass) {
      if (pass.badge_flags) {
        if (pass.badge_flags.system_completed) {
          return "#DF3C3B"
        }
        if (pass.badge_flags.min_time) {
          return "#F4EA65"
        }
        if (pass.badge_flags.extended) {
          return "#672C91"
        }
      }
      return "transparent"
    },
    setTabFilterValue(type) {
      if (type !== this.filterParams.tab_filter) {
        this.filterParams.tab_filter = type
        this.requestPassHistory()
      }
    },
    closeModal() {
      this.modal = {
        isVisible: {
          passTimeChange: false,
          comments: false,
          todayPasses: false
        },
        activePass: null
      }
    },
    setActiveModalPass(pass, modal) {
      if (pass) {
        this.modal.isVisible[modal] = true
        this.modal.activePass = pass
      }
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.requestPassHistory(this.pagination.activePage)
      this.$helpers.scrollToTop()
    },
    isPassEditable(pass) {
      if (this.currentUser) {
        if (pass.approved_by === this.currentUser.id) {
          return true
        }
        if (pass.completed_by === this.currentUser.id) {
          return true
        }
        if (pass.approved_with == "autopass") {
          const autoPassesRooms =
            this.autoPassLimits.auto_pass_preference_to_user.map(
              (el) => el.auto_pass.room_id
            )
          if (autoPassesRooms.includes(pass.from.id)) {
            return true
          }
          if (autoPassesRooms.includes(pass.to.id)) {
            return true
          }
          return false
        }
      }
      return false
    },
    searchByQuery(value, type) {
      clearInterval(this.searchTimeOut)
      this.searchTimeOut = setTimeout(() => {
        this.handleQuerySearch(value, type)
      }, 500)
    },
    handleQuerySearch(value, type) {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        this.filterParams.search_query = ""
        if (type && type === "column") {
          this.columnSearchKeys.globalQuery = ""
          for (const key in this.columnSearchKeys) {
            if (Object.hasOwnProperty.call(this.columnSearchKeys, key)) {
              const columnData = this.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  this.filterParams.search_query =
                    this.filterParams.search_query +
                    `${col}:"${columnData.value}", `
                })
              }
            }
          }
          this.filterParams.search_query = this.filterParams.search_query.slice(
            0,
            -2
          )
        } else {
          this.resetSearchQuery()
          this.columnSearchKeys.globalQuery = query
          this.filterParams.search_query = query ? '"' + query + '"' : query
        }
        this.requestPassHistory()
      }
    },
    sortByColumn(column) {
      const columnName = this.columnSearchKeys[column.column]
        ? this.columnSearchKeys[column.column].sortBy
        : column.column
      this.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`
      this.requestPassHistory()
    },
    advancedFilter(isTabular) {
      this.isPassView = true
      this.resetSearchQuery()
      this.requestPassHistory()
      this.isTabular = isTabular
    },
    resetSearchQuery() {
      this.filterParams.search_query = ""
      this.columnSearchKeys = {
        globalQuery: "",
        created_date: {
          value: "",
          column: ["created_date"],
          sortBy: "created_date"
        },
        student_sis_id: {
          value: "",
          column: ["student_sis_id"],
          sortBy: "student_sis_id"
        },
        user: { value: "", column: ["user.name"], sortBy: "user.last_name" },
        first_name: {
          value: "",
          column: ["user.first_name"],
          sortBy: "user.first_name"
        },
        last_name: {
          value: "",
          column: ["user.last_name"],
          sortBy: "user.last_name"
        },
        email: { value: "", column: ["user.email"], sortBy: "user.email" },

        out_location: { value: "", column: ["from.name"], sortBy: "from.name" },
        out_teacher: {
          value: "",
          column: ["approved_by_user.first_name"],
          sortBy: "approved_by_user.first_name"
        },
        out_time: {
          value: "",
          column: ["tabular_view.approve_time"],
          sortBy: "elastic_advanced_fields.approve_time_only"
        },
        in_location: { value: "", column: ["to.name"], sortBy: "to.name" },
        in_teacher: {
          value: "",
          column: ["completed_by_user.first_name"],
          sortBy: "completed_by_user.first_name"
        },
        in_time: {
          value: "",
          column: ["tabular_view.completed_time"],
          sortBy: "elastic_advanced_fields.completed_time_only"
        },

        child_out_location: {
          value: "",
          column: ["child.from.name"],
          sortBy: "child.from.name"
        },
        child_out_teacher: {
          value: "",
          column: ["child.approved_by_user.first_name"],
          sortBy: "child.approved_by_user.first_name"
        },
        child_out_time: {
          value: "",
          column: ["tabular_view.approve_child_time"],
          sortBy: "elastic_advanced_fields.approve_child_time_only"
        },
        child_in_location: {
          value: "",
          column: ["child.to.name"],
          sortBy: "child.to.name"
        },
        child_in_teacher: {
          value: "",
          column: ["child.completed_by_user.first_name"],
          sortBy: "child.completed_by_user.first_name"
        },
        child_in_time: {
          value: "",
          column: ["tabular_view.completed_child_time"],
          sortBy: "elastic_advanced_fields.completed_child_time_only"
        },

        approved_at: {
          value: "",
          column: ["from.name"],
          sortBy: "elastic_advanced_fields.approve_time_only"
        },
        completed_at: {
          value: "",
          column: ["to.name"],
          sortBy: "elastic_advanced_fields.completed_time_only"
        },
        child_approved_at: {
          value: "",
          column: ["child.from.name"],
          sortBy: "elastic_advanced_fields.approve_child_time_only"
        },
        child_completed_at: {
          value: "",
          column: ["child.to.name"],
          sortBy: "elastic_advanced_fields.completed_child_time_only"
        },
        total_time: { value: "", column: ["total_time"], sortBy: "total_time" },
        type: { value: "", column: ["type"], sortBy: "type" },
        comments: {
          value: "",
          column: ["comments.comment"],
          sortBy: "created_date"
        },
        expired_at: { value: "", column: ["expired_at"], sortBy: "expired_at" }
      }
    },
    setCalendarRange(range, togglePopover) {
      const isCustomRange = range && range.label == "Custom range"

      this.calendar.isCalendarVisible = isCustomRange
      this.calendar.isRangesVisible = false // !isCustomRange

      this.calendar.activeRange = range
      this.filterParams.dates = range.range
        ? range.range
        : this.filterParams.dates
      if (this.calendar.isCalendarVisible) {
        togglePopover()
      }
    },
    showCalendarRanges(hidePopover, togglePopover) {
      if (this.calendar.isCalendarVisible) {
        hidePopover()
      }
      let isCurrentRange = false
      if (this.gradeYears.length && this.filterParams.grade_years) {
        // show dropdown if current school year is selected (the last one in our collection)
        isCurrentRange =
          this.gradeYears[this.gradeYears.length - 1].label ===
          this.filterParams.grade_years.label
      }
      this.calendar.isRangesVisible = isCurrentRange
      this.calendar.isCalendarVisible = !isCurrentRange
      if (this.calendar.isCalendarVisible) {
        togglePopover()
      }
    },
    setCalendarDates() {
      this.filterParams.dates = {
        start: this.minDate,
        end: this.maxDate
      }
      this.calendar.activeRange = this.optionalDateRanges[6]
    },
    onContainerClick(e) {
      const target = e.target
      const classList =
        target.classList.contains("date-calendar-option") ||
        target.classList.contains("flaticon-calendar") ||
        target.classList.contains("field-icon")
      if (target && !classList) {
        this.calendar.isRangesVisible = false
      }
    },
    getPassStatus(pass) {
      if (pass.badge_flags) {
        if (
          pass.badge_flags.ended ||
          pass.badge_flags.system_completed ||
          pass.badge_flags.min_time
        ) {
          return { label: "Ended", class: "ended" }
        }
        // if(pass.badge_flags.min_time){
        //   return {label: this.passesTimeSettings ? this.passesTimeSettings.min_time : 'Min time', class: 'min-time'};
        // }
        if (pass.badge_flags.missed) {
          return { label: "Missed Apt", class: "missed-apt" }
        }
        if (pass.badge_flags.canceled) {
          return { label: "Canceled", class: "canceled" }
        }
        if (pass.badge_flags.missed_request) {
          return { label: "Missed Request", class: "missed" }
        }
      }
      return ""
    },
    onPassTimeChange(pass) {
      this.modal.isVisible.passTimeChange = false
      this.$store.commit("passes/UPDATE_PASS_HISTORY", pass)
    },
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.passHistories &&
            this.passHistories.length &&
            this.pagination.total > this.passHistories.length
          ) {
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.requestPassHistory(
                this.pagination.activePage,
                false,
                true,
                false
              )
            }
          }
        }
      }
    },
    initDataSet() {
      if (!this.passesTimeSettings) {
        this.$store.dispatch("passes/getPassesTimeSetting")
      }
      this.filterParams.grade_years = this.gradeYears
        ? this.gradeYears[this.gradeYears.length - 1]
        : this.filterParams.grade_years
      this.activeTableTab = this.tableTabs[0]
    },
    onCommentCreated(data) {
      this.$store.commit("passes/UPDATE_PASS_HISTORY_COMMENT", data)
    }
  }
}
</script>
